import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";

const ORDER_STATUSES = [
  "New", "Awaiting Payment", "Payment Submitted", "Payment Confirmed",
  "Contacted", "Confirmed", "Preparing", "Out for Delivery",
  "Delivered", "Cancelled", "Refunded",
];

const PAYMENT_STATUSES = [
  "Pending Payment", "Payment Submitted", "Payment Confirmed",
  "Payment Failed", "Cash on Delivery", "Refunded", "Cancelled",
];

const SHIPPING_STATUSES = [
  "Not Prepared", "Preparing", "Ready for Pickup", "Out for Delivery",
  "Delivered", "Failed Delivery", "Returned", "Cancelled",
];

const STATUS_COLORS = {
  // order statuses
  "New": "#38BDF8",
  "Awaiting Payment": "#FACC15",
  "Payment Submitted": "#FBBF24",
  "Payment Confirmed": "#A3E635",
  "Contacted": "#FBBF24",
  "Confirmed": "#A3E635",
  "Preparing": "#F472B6",
  "Out for Delivery": "#818CF8",
  "Delivered": "#4ADE80",
  "Cancelled": "#F87171",
  "Refunded": "#94A3B8",
  // payment statuses (some overlap with order)
  "Pending Payment": "#FACC15",
  "Payment Failed": "#F87171",
  "Cash on Delivery": "#34D399",
  // shipping statuses
  "Not Prepared": "#CBD5E1",
  "Ready for Pickup": "#A78BFA",
  "Failed Delivery": "#F87171",
  "Returned": "#94A3B8",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    const url = filter === "all" ? "/admin/orders" : `/admin/orders?status=${encodeURIComponent(filter)}`;
    api
      .get(url)
      .then((r) => setOrders(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const refreshSelected = (updated) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setSelected(updated);
  };

  const updateOrderStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      refreshSelected(data);
    } catch (_) {
      toast.error("Failed to update status");
    }
  };

  const updatePayment = async (id, payload) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}/payment`, payload);
      toast.success("Payment updated");
      refreshSelected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update payment");
    }
  };

  const updateShipping = async (id, payload) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}/shipping`, payload);
      toast.success("Shipping updated");
      refreshSelected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update shipping");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold">Orders</h1>
          <p className="text-sm text-slate-600">Track, contact, payments, shipping — all in one place.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="orders-status-filter">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ORDER_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="neo-card bg-white overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead style={{ background: "#FDE047", borderBottom: "2px solid #0F172A" }}>
              <tr className="text-left">
                <Th>Order #</Th>
                <Th>Customer</Th>
                <Th>City</Th>
                <Th>Total</Th>
                <Th>Payment</Th>
                <Th>Pay Status</Th>
                <Th>Shipping</Th>
                <Th>Order Status</Th>
                <Th>Date</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-6 text-center text-slate-500">Loading…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={9} className="p-10 text-center text-slate-500" data-testid="orders-empty">No orders yet.</td></tr>
              ) : (
                orders.map((o) => (
                  <tr
                    key={o.id}
                    className="border-b border-slate-200 hover:bg-yellow-50 cursor-pointer"
                    onClick={() => setSelected(o)}
                    data-testid={`order-row-${o.order_number}`}
                  >
                    <Td><span className="font-mono font-semibold">{o.order_number}</span></Td>
                    <Td>
                      <div className="font-semibold">{o.full_name}</div>
                      <div className="text-xs text-slate-500">{o.phone}</div>
                    </Td>
                    <Td>
                      <div className="text-sm">{o.city}</div>
                      <div className="text-xs text-slate-500">{o.area}</div>
                    </Td>
                    <Td><span className="font-bold">{o.total.toFixed(0)} EGP</span></Td>
                    <Td><span className="text-xs">{o.payment_method}</span></Td>
                    <Td><StatusBadge status={o.payment_status} /></Td>
                    <Td><StatusBadge status={o.shipping_status} /></Td>
                    <Td><StatusBadge status={o.status} /></Td>
                    <Td>
                      <span className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</span>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <OrderDetailsDialog
          order={selected}
          onClose={() => setSelected(null)}
          onUpdateStatus={(s) => updateOrderStatus(selected.id, s)}
          onUpdatePayment={(p) => updatePayment(selected.id, p)}
          onUpdateShipping={(p) => updateShipping(selected.id, p)}
        />
      )}
    </div>
  );
}

function whatsappLink(order) {
  const digits = (order.whatsapp || order.phone || "").replace(/\D/g, "");
  const local = digits.replace(/^0+/, "");
  const intl = local.startsWith("20") ? local : `20${local}`;
  const lines = [
    `Pet Munchies — Order ${order.order_number}`,
    `Customer: ${order.full_name}`,
    "Items:",
    ...order.items.map((it) => `- ${it.name} x${it.quantity} = ${(it.price * it.quantity).toFixed(0)} EGP`),
    `Subtotal: ${order.subtotal.toFixed(0)} EGP`,
    order.discount_code ? `Discount (${order.discount_code}): -${order.discount_amount.toFixed(0)} EGP` : null,
    `Shipping (${order.shipping_method}): ${order.shipping_fee} EGP`,
    `Total: ${order.total.toFixed(0)} EGP`,
    `Address: ${order.city}, ${order.area} — ${order.address}`,
    `Payment: ${order.payment_method} (${order.payment_status || "—"})`,
  ].filter(Boolean);
  return `https://wa.me/${intl}?text=${encodeURIComponent(lines.join("\n"))}`;
}

function OrderDetailsDialog({ order, onClose, onUpdateStatus, onUpdatePayment, onUpdateShipping }) {
  const [paymentForm, setPaymentForm] = useState({
    payment_status: order.payment_status || "Pending Payment",
    payment_reference: order.payment_reference || "",
    amount_confirmed: order.amount_confirmed ?? 0,
    payment_confirmation_notes: order.payment_confirmation_notes || "",
  });

  const [shippingForm, setShippingForm] = useState({
    shipping_status: order.shipping_status || "Not Prepared",
    shipping_admin_notes: order.shipping_admin_notes || "",
    shipping_eta: order.shipping_eta || "",
  });

  // Sync internal form when parent updates the order
  useEffect(() => {
    setPaymentForm({
      payment_status: order.payment_status || "Pending Payment",
      payment_reference: order.payment_reference || "",
      amount_confirmed: order.amount_confirmed ?? 0,
      payment_confirmation_notes: order.payment_confirmation_notes || "",
    });
    setShippingForm({
      shipping_status: order.shipping_status || "Not Prepared",
      shipping_admin_notes: order.shipping_admin_notes || "",
      shipping_eta: order.shipping_eta || "",
    });
  }, [order]);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-auto"
        data-testid="order-details-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2 flex-wrap">
            Order {order.order_number}
            <StatusBadge status={order.status} />
          </DialogTitle>
          <DialogDescription>Customer, items, payment & shipping management.</DialogDescription>
        </DialogHeader>

        <a
          href={whatsappLink(order)}
          target="_blank"
          rel="noopener noreferrer"
          className="neo-btn inline-flex items-center gap-2 px-4 py-2 text-white font-bold w-fit"
          style={{ background: "#25D366" }}
          data-testid="send-whatsapp-btn"
        >
          <MessageCircle size={16} /> Send to WhatsApp
        </a>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <Section title="Customer">
            <KV k="Name" v={order.full_name} />
            <KV k="Phone" v={order.phone} />
            <KV k="WhatsApp" v={order.whatsapp || "—"} />
            <KV k="Email" v={order.email || "—"} />
          </Section>
          <Section title="Shipping address">
            <KV k="City" v={order.city} />
            <KV k="Area" v={order.area} />
            <KV k="Address" v={order.address} />
            <KV k="Building/notes" v={order.address_notes || "—"} />
            <KV k="Preferred time" v={order.delivery_time || "—"} />
          </Section>
          <Section title="Order">
            <KV k="Method" v={order.payment_method} />
            <KV k="Original Subtotal" v={`${order.subtotal.toFixed(0)} EGP`} />
            {order.discount_code ? (
              <>
                <KV
                  k={`Discount Code (${order.discount_percent}%)`}
                  v={<span data-testid="order-discount-code" className="text-emerald-600">{order.discount_code}</span>}
                />
                <KV
                  k="Discount Amount"
                  v={<span data-testid="order-discount-amount" className="text-emerald-600">- {order.discount_amount.toFixed(0)} EGP</span>}
                />
              </>
            ) : null}
            <KV k="Shipping" v={`${order.shipping_method} (${order.shipping_eta || "—"}) — ${order.shipping_fee} EGP`} />
            <KV k="Total" v={`${order.total.toFixed(0)} EGP`} />
            <KV k="Notes" v={order.order_notes || "—"} />
            <KV k="Date" v={new Date(order.created_at).toLocaleString()} />
          </Section>
          <Section title="Items">
            <ul className="space-y-1">
              {order.items.map((it, i) => (
                <li key={i} className="flex justify-between border-b border-dashed border-slate-200 py-1">
                  <span>{it.name} <span className="text-slate-500">× {it.quantity}</span></span>
                  <span className="font-semibold">{(it.price * it.quantity).toFixed(0)} EGP</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        {/* Order Status update */}
        <div className="mt-5">
          <div className="font-display text-sm font-semibold mb-2">Order Status</div>
          <div className="flex flex-wrap gap-2" data-testid="order-status-buttons">
            {ORDER_STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => onUpdateStatus(s)}
                className="neo-btn px-3 py-1.5 text-xs"
                style={{
                  background: STATUS_COLORS[s] || "#cbd5e1",
                  color: "#0F172A",
                  outline: order.status === s ? "3px solid #0F172A" : "none",
                }}
                data-testid={`status-btn-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Payment management */}
        <div className="mt-6 neo-flat p-4 bg-white" data-testid="payment-management">
          <div className="font-display text-base font-bold mb-3 flex items-center justify-between">
            <span>Payment Management</span>
            <StatusBadge status={paymentForm.payment_status} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Payment Status">
              <Select
                value={paymentForm.payment_status}
                onValueChange={(v) => setPaymentForm((s) => ({ ...s, payment_status: v }))}
              >
                <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Amount Confirmed (EGP)">
              <Input
                type="number" min="0" step="1"
                value={paymentForm.amount_confirmed}
                onChange={(e) => setPaymentForm((s) => ({ ...s, amount_confirmed: e.target.value }))}
                data-testid="amount-confirmed-input"
              />
            </Field>
            <Field label="Payment Reference / Transaction ID" full>
              <Input
                value={paymentForm.payment_reference}
                onChange={(e) => setPaymentForm((s) => ({ ...s, payment_reference: e.target.value }))}
                data-testid="payment-reference-input"
              />
            </Field>
            <Field label="Confirmation Notes" full>
              <Textarea
                rows={2}
                value={paymentForm.payment_confirmation_notes}
                onChange={(e) => setPaymentForm((s) => ({ ...s, payment_confirmation_notes: e.target.value }))}
                data-testid="payment-notes-input"
              />
            </Field>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => onUpdatePayment({
                ...paymentForm,
                amount_confirmed: Number(paymentForm.amount_confirmed) || 0,
              })}
              className="neo-btn px-4 py-2 text-white font-bold"
              style={{ background: "var(--pm-dog)" }}
              data-testid="save-payment-btn"
            >
              Save Payment
            </button>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Amount expected: <strong>{(order.amount_expected ?? order.total).toFixed(0)} EGP</strong>
          </div>
        </div>

        {/* Shipping management */}
        <div className="mt-4 neo-flat p-4 bg-white" data-testid="shipping-management">
          <div className="font-display text-base font-bold mb-3 flex items-center justify-between">
            <span>Shipping Management</span>
            <StatusBadge status={shippingForm.shipping_status} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Shipping Status">
              <Select
                value={shippingForm.shipping_status}
                onValueChange={(v) => setShippingForm((s) => ({ ...s, shipping_status: v }))}
              >
                <SelectTrigger data-testid="shipping-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHIPPING_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Estimated Delivery Time">
              <Input
                value={shippingForm.shipping_eta}
                onChange={(e) => setShippingForm((s) => ({ ...s, shipping_eta: e.target.value }))}
                placeholder="e.g. 1 business day"
                data-testid="shipping-eta-input"
              />
            </Field>
            <Field label="Internal Shipping Notes" full>
              <Textarea
                rows={2}
                value={shippingForm.shipping_admin_notes}
                onChange={(e) => setShippingForm((s) => ({ ...s, shipping_admin_notes: e.target.value }))}
                placeholder="Courier name, tracking number, anything for the team"
                data-testid="shipping-notes-input"
              />
            </Field>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => onUpdateShipping(shippingForm)}
              className="neo-btn px-4 py-2 text-white font-bold"
              style={{ background: "var(--pm-cat)" }}
              data-testid="save-shipping-btn"
            >
              Save Shipping
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span
      className="inline-block text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap"
      style={{
        background: STATUS_COLORS[status] || "#cbd5e1",
        color: "#0F172A",
        border: "2px solid #0F172A",
      }}
    >
      {status}
    </span>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-xs uppercase tracking-wider font-bold">{children}</th>;
}
function Td({ children }) { return <td className="px-4 py-3">{children}</td>; }

function Section({ title, children }) {
  return (
    <div className="neo-flat p-3 bg-white">
      <div className="font-display font-semibold text-sm mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex gap-2 justify-between">
      <span className="text-slate-500 shrink-0">{k}</span>
      <span className="font-semibold text-end break-words">{v}</span>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      {children}
    </label>
  );
}
